import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { HideOnWindowScrollDirective } from '../../directives/hide-on-window-scroll/hide-on-window-scroll.directive';
import { MBadgeComponent } from '../m-badge';
import { MBrandPickerComponent } from '../m-brand-picker';
import { MMegaMenuComponent } from '../m-mega-menu';
import { MNavTriggerComponent } from '../m-nav-trigger';
import type {
  BrandPickerSelectEvent,
  TopNavAction,
  TopNavActionClickEvent,
  TopNavAvatar,
  TopNavBrandPicker,
  TopNavLogo,
  TopNavNotification
} from './m-top-nav.types';

@Component({
  selector: 'm-top-nav',
  templateUrl: './m-top-nav.component.html',
  styleUrls: ['./m-top-nav.component.scss'],
  imports: [
    NgTemplateOutlet,
    MenuModule,
    HideOnWindowScrollDirective,
    MBadgeComponent,
    MBrandPickerComponent,
    MMegaMenuComponent,
    MNavTriggerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MTopNavComponent {
  readonly defaultLogoSrc = 'assets/images/measured-m-dark.svg';

  readonly logo = input<TopNavLogo>({ alt: 'Measured' });
  readonly brandPicker = input<TopNavBrandPicker>();
  readonly primaryActions = input<TopNavAction[]>([]);
  readonly secondaryActions = input<TopNavAction[]>([]);
  readonly trailingAction = input<TopNavAction>();
  readonly avatar = input<TopNavAvatar>();
  readonly postAvatarAction = input<TopNavAction>();

  readonly actionClicked = output<TopNavActionClickEvent>();
  readonly brandSelected = output<BrandPickerSelectEvent>();
  readonly brandPickerOpened = output<void>();

  readonly openMenuActionId = signal<string | null>(null);

  constructor(private readonly router: Router) {}

  onLogoClick(): void {
    const routerLink = this.logo().routerLink;
    if (!routerLink) {
      return;
    }
    const commands = Array.isArray(routerLink) ? routerLink : [routerLink];
    this.router.navigate(commands).catch(console.error);
  }

  onActionClick(action: TopNavAction, event: Event): void {
    this.actionClicked.emit({ action, event });
  }

  onBrandSelected(event: BrandPickerSelectEvent): void {
    this.brandSelected.emit(event);
  }

  hasMenu(action: TopNavAction): boolean {
    return !!action.menuItems && action.menuItems.length > 0;
  }

  hasMegaMenu(action: TopNavAction): boolean {
    return !!action.megaMenuItems && action.megaMenuItems.length > 0;
  }

  hasMegaMenuBadge(action: TopNavAction): boolean {
    return !!action.megaMenuItems?.some((i) => !!i.badge);
  }

  showNotificationDot(notification: TopNavNotification): boolean {
    return !!notification.showDot || (notification.count ?? 0) > 0;
  }
}
